import {
  Box,
  Spinner,
  ModalFooter,
  VStack,
  IconButton,
  Switch,
  FormControl,
  FormLabel,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Input,
  HStack,
  Collapse,
  useColorModeValue,
  Text,
  FormHelperText,
  Button,
  Stack,
} from "@chakra-ui/react";
import { FiX, FiRefreshCw } from "react-icons/fi";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";

import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  ChatSession,
  GenerateImageDecor8Request,
  IOutputFormat,
  TModelSearchResult,
} from "types";
import { ModelSearch } from "components/model-search/ModelSearch";
interface Decor8ImageSettingsModalProps {
  decorType: "prime" | "stage";
  setDecorType: (decorType: "prime" | "stage") => void;
  roomType: GenerateImageDecor8Request["roomType"];
  setRoomType: (roomType: GenerateImageDecor8Request["roomType"]) => void;
  designStyle: GenerateImageDecor8Request["designStyle"];
  setDesignStyle: (
    designStyle: GenerateImageDecor8Request["designStyle"]
  ) => void;
  numImages: number;
  setNumImages: (numImages: number) => void;
  colorScheme: GenerateImageDecor8Request["colorScheme"];
  setColorScheme: (
    colorScheme: GenerateImageDecor8Request["colorScheme"]
  ) => void;
  specialityDecor: GenerateImageDecor8Request["specialityDecor"];
  setSpecialityDecor: (
    specialityDecor: GenerateImageDecor8Request["specialityDecor"]
  ) => void;
  scaleFactor: GenerateImageDecor8Request["scaleFactor"];
  setScaleFactor: (
    scaleFactor: GenerateImageDecor8Request["scaleFactor"]
  ) => void;
}

interface ImageSettingsModalProps {
  selectedModel: string;
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  isImageSettingsOpen: boolean;
  onImageSettingsClose: () => void;
  showNegativePrompt: boolean;
  setShowNegativePrompt: (show: boolean) => void;
  negativePrompt: string;
  setNegativePrompt: (prompt: string) => void;
  isHD: boolean;
  setIsHD: (isHD: boolean) => void;
  selectedSize: string;
  setSelectedSize: (size: string) => void;
  width: number;
  setWidth: (width: number) => void;
  height: number;
  setHeight: (height: number) => void;
  format: IOutputFormat;
  setFormat: (format: IOutputFormat) => void;
  numImages: number;
  setNumImages: (numImages: number) => void;
  steps: number;
  setSteps: (steps: number) => void;
  cfgScale: number;
  setCfgScale: (scale: number) => void;
  seed: number;
  setSeed: (seed: number) => void;
  strength: number;
  setStrength: (strength: number) => void;
  promptImageFile: string | null;
  scheduler: string;
  setScheduler: (scheduler: string) => void;
  selectedRunwareModel: TModelSearchResult | null;
  setSelectedRunwareModel: (model: TModelSearchResult | null) => void;
  currentSession: ChatSession;
  setPromptImageFile: (promptImageFile: string | null) => void;
  compressImage: (file: File) => Promise<Blob>;
  fileToBase64: (file: File) => Promise<unknown>;
}

type SizePreset = {
  width: number;
  height: number;
};

type SizePresetKey =
  | "Square"
  | "Portrait 3:4"
  | "Portrait 2:3"
  | "Portrait 9:16"
  | "Landscape 4:3"
  | "Landscape 3:2"
  | "Landscape 16:9"
  | "Custom";

const SCHEDULERS = [
  "Default",
  "DDIMScheduler",
  "DDIMInverseScheduler",
  "DDPMScheduler",
  "DEISMultistepScheduler",
  "DPMSolverSinglestepScheduler",
  "DPMSolverSDEScheduler",
  "DPMSolverMultistepScheduler",
  "DPMSolverSDEScheduler",
  "HeunDiscreteScheduler",
  "KDPM2DiscreteScheduler",
  "KDPM2AncestralDiscreteScheduler",
  "KarrasVeScheduler",
  "LMSDiscreteScheduler",
  "PNDMScheduler",
  "ScoreSdeVeScheduler",
  "IPNDMScheduler",
  "EulerDiscreteScheduler",
  "EulerAncestralDiscreteScheduler",
  "EDMEulerScheduler",
  "FlowMatchEulerDiscreteScheduler",
  "VQDiffusionScheduler",
  "UniPCMultistepScheduler",
  "RePaintScheduler",
  "DPM++ 2M Karras",
  "DPM++ 2M SDE Karras",
  "DPM++ 2M SDE",
  "LCMScheduler",
  "EDMDPMSolverMultistepScheduler",
  "TCDScheduler",
];

const SIZE_PRESETS: Record<SizePresetKey, SizePreset> = {
  Square: { width: 512, height: 512 },
  "Portrait 3:4": { width: 512, height: 683 },
  "Portrait 2:3": { width: 512, height: 768 },
  "Portrait 9:16": { width: 512, height: 910 },
  "Landscape 4:3": { width: 683, height: 512 },
  "Landscape 3:2": { width: 768, height: 512 },
  "Landscape 16:9": { width: 910, height: 512 },
  Custom: { width: 512, height: 512 },
};

const SIZE_PRESETS_PTBR = [
  {
    label: "Quadrado",
    value: "Square",
  },
  {
    label: "Retrato 3:4",
    value: "Portrait 3:4",
  },
  {
    label: "Retrato 2:3",
    value: "Portrait 2:3",
  },
  {
    label: "Retrato 9:16",
    value: "Portrait 9:16",
  },
  {
    label: "Paisagem 4:3",
    value: "Landscape 4:3",
  },
  {
    label: "Paisagem 3:2",
    value: "Landscape 3:2",
  },
  {
    label: "Paisagem 16:9",
    value: "Landscape 16:9",
  },
  {
    label: "Personalizado",
    value: "Custom",
  },
];

export const ImageSettingsModal = ({
  selectedModel,
  isOpen,
  onClose,
  isLoading,
  isImageSettingsOpen,
  onImageSettingsClose,
  showNegativePrompt,
  setShowNegativePrompt,
  negativePrompt,
  setNegativePrompt,
  isHD,
  setIsHD,
  selectedSize,
  setSelectedSize,
  width,
  setWidth,
  height,
  setHeight,
  format,
  setFormat,
  numImages,
  setNumImages,
  steps,
  setSteps,
  cfgScale,
  setCfgScale,
  seed,
  setSeed,
  strength,
  setStrength,
  promptImageFile,
  scheduler,
  setScheduler,
  decorType,
  setDecorType,
  roomType,
  setRoomType,
  designStyle,
  setDesignStyle,
  colorScheme,
  setColorScheme,
  specialityDecor,
  setSpecialityDecor,
  scaleFactor,
  setScaleFactor,
  selectedRunwareModel,
  setSelectedRunwareModel,
  currentSession,
  setPromptImageFile,
  compressImage,
  fileToBase64,
}: ImageSettingsModalProps & Decor8ImageSettingsModalProps) => {
  const { t, i18n } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const inputBg = useColorModeValue("white", "gray.800");

  const getRandomNumber = (start: number, end: number) =>
    Math.floor(Math.random() * (end - start + 1)) + start;

  const handleRandomizeSeed = () => {
    setSeed(getRandomNumber(1, Number.MAX_SAFE_INTEGER));
  };

  const translateSizePreset = (sizePreset: SizePresetKey) => {
    const lang = i18n.language;
    if (lang === "pt-BR") {
      const preset = SIZE_PRESETS_PTBR.find(
        (preset) => preset.value === sizePreset
      );
      return preset ? preset.label : sizePreset;
    }
    return sizePreset;
  };

  //   const handleRandomizeScheduler = () => {
  //     setScheduler(Math.floor(Math.random() * 1000000));
  //   };

  useEffect(() => {
    if (isHD) {
      setWidth(1024);
      setHeight(1024);
    } else {
      setWidth(512);
      setHeight(512);
    }
  }, [isHD]);

  useEffect(() => {
    if (
      selectedSize !== "Custom" &&
      SIZE_PRESETS[selectedSize as SizePresetKey]
    ) {
      const preset = SIZE_PRESETS[selectedSize as SizePresetKey];
      setWidth(isHD ? preset.width * 2 : preset.width);
      setHeight(isHD ? preset.height * 2 : preset.height);
    }
  }, [selectedSize, isHD]);

  const handleModelSelect = (model: TModelSearchResult) => {
    console.log(model);
    setSelectedRunwareModel(model);
  };

  return (
    <Modal
      isOpen={isImageSettingsOpen}
      onClose={onImageSettingsClose}
      size="5xl"
      motionPreset="scale"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        as={motion.div}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: "0.2s" }}
      >
        <ModalHeader>{t("chat.imageSettings.title")}</ModalHeader>
        <ModalCloseButton />
        {selectedModel.includes("runware") && (
          <ModalBody>
            {isLoading ? (
              <Box textAlign="center" py={4}>
                <Spinner />
              </Box>
            ) : (
              <Stack direction={{ base: "column", md: "row" }} spacing={2}>
                {selectedModel.includes("civitai") && (
                  <VStack spacing={4} align="stretch" minWidth="40%">
                    <FormControl>
                      <FormLabel>
                        {t("chat.imageSettings.modelSearch")}
                      </FormLabel>
                      <ModelSearch onModelSelect={handleModelSelect} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>
                        {t("chat.imageSettings.selectedRunwareModel")}
                      </FormLabel>
                      <Input
                        value={`${selectedRunwareModel?.name} - ${
                          selectedRunwareModel?.air
                        } - ${selectedRunwareModel?.architecture.toUpperCase()}`}
                        disabled
                        bg={inputBg}
                      />
                    </FormControl>
                  </VStack>
                )}
                <VStack spacing={4} align="stretch">
                  {currentSession?.sessionType === "IMAGE_CHAT" &&
                    selectedModel.includes("runware") && (
                      <FormControl>
                        <FormLabel>
                          {t("chat.imageSettings.uploadReferenceImage")}
                        </FormLabel>
                        <HStack
                          // border="2px dashed"
                          // borderColor="gray.300"
                          // borderRadius="md"
                          // p={4}
                          textAlign="center"
                          _hover={{ borderColor: "gray.400" }}
                          cursor="pointer"
                        >
                          {/* <Text>{t("chat.imageSettings.dragAndDrop")}</Text> */}
                          {/* <Text>{t("chat.imageSettings.or")}</Text> */}
                          {/* Display the image preview if promptImageFile exists */}
                          {promptImageFile && (
                            <img
                              src={promptImageFile}
                              alt="Preview"
                              style={{
                                maxWidth: "50px",
                                // margin: "10px auto",
                                borderRadius: "5px",
                              }}
                            />
                          )}
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                try {
                                  // Compress the image first
                                  const compressedBlob = await compressImage(
                                    file
                                  );
                                  const base64Result = await fileToBase64(
                                    new File([compressedBlob], file.name, {
                                      type: "image/jpeg",
                                    })
                                  );
                                  setPromptImageFile(base64Result as string);
                                } catch (error) {
                                  console.error(
                                    "Error processing image:",
                                    error
                                  );
                                }
                              }
                            }}
                            placeholder={t("chat.imageSettings.imageUrl")}
                          />
                        </HStack>
                      </FormControl>
                    )}
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">
                      {t("chat.imageSettings.negativePromptToggle")}
                    </FormLabel>
                    <Switch
                      isChecked={showNegativePrompt}
                      onChange={(e) => setShowNegativePrompt(e.target.checked)}
                    />
                  </FormControl>

                  <Collapse in={showNegativePrompt}>
                    <FormControl>
                      {/* <FormLabel>
                        {t("chat.imageSettings.negativePrompt")}
                      </FormLabel> */}
                      <Input
                        value={negativePrompt}
                        onChange={(e) => setNegativePrompt(e.target.value)}
                        placeholder={t(
                          "chat.imageSettings.negativePromptPlaceholder"
                        )}
                        bg={inputBg}
                      />
                      <FormHelperText>
                        {t("chat.imageSettings.negativePromptHelpText")}
                      </FormHelperText>
                    </FormControl>
                  </Collapse>

                  <Box
                    // display="flex"
                    // alignItems="center"
                    border="1px dashed gray"
                    borderRadius="md"
                    p={2}
                  >
                    <Text fontSize="sm" color="gray.500">
                      {t("chat.imageSettings.size")}
                    </Text>
                    <FormControl>
                      <Stack
                        direction={{ base: "column", md: "row" }}
                        spacing={2}
                        alignItems="center"
                      >
                        <FormLabel mb="0">
                          {t("chat.imageSettings.hd")}
                        </FormLabel>
                        <Switch
                          isChecked={isHD}
                          onChange={(e) => setIsHD(e.target.checked)}
                        />
                        {/* <FormLabel>{t("chat.imageSettings.size")}</FormLabel> */}
                        <Select
                          value={selectedSize}
                          onChange={(e) => setSelectedSize(e.target.value)}
                          bg={inputBg}
                        >
                          {Object.keys(SIZE_PRESETS).map((size) => (
                            <option key={size} value={size}>
                              {translateSizePreset(size as SizePresetKey)}
                            </option>
                          ))}
                        </Select>
                        <FormHelperText>
                          {t("chat.imageSettings.sizeHelpText")}
                        </FormHelperText>
                      </Stack>
                    </FormControl>

                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>{t("chat.imageSettings.width")}</FormLabel>
                        <NumberInput
                          value={width}
                          onChange={(_, value) => setWidth(value)}
                          min={64}
                          max={2048}
                          step={64}
                          isDisabled={selectedSize !== "Custom"}
                        >
                          <NumberInputField bg={inputBg} />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      <FormControl>
                        <FormLabel>{t("chat.imageSettings.height")}</FormLabel>
                        <NumberInput
                          value={height}
                          onChange={(_, value) => setHeight(value)}
                          min={64}
                          max={2048}
                          step={64}
                          isDisabled={selectedSize !== "Custom"}
                        >
                          <NumberInputField bg={inputBg} />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </HStack>
                  </Box>

                  <FormControl>
                    <FormLabel>{t("chat.imageSettings.format")}</FormLabel>
                    <Select
                      value={format}
                      onChange={(e) =>
                        setFormat(e.target.value as IOutputFormat)
                      }
                      bg={inputBg}
                    >
                      <option value="JPG">JPG</option>
                      <option value="PNG">PNG</option>
                      <option value="WEBP">WEBP</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>{t("chat.imageSettings.numImages")}</FormLabel>
                    <NumberInput
                      value={numImages}
                      onChange={(_, value) => setNumImages(value)}
                      min={1}
                      max={20}
                      step={1}
                    >
                      <NumberInputField bg={inputBg} />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormHelperText>
                      {t("chat.imageSettings.numImagesHelpText")}
                    </FormHelperText>
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">
                      {t("chat.imageSettings.advancedControls")}
                    </FormLabel>
                    <Switch
                      isChecked={showAdvanced}
                      onChange={(e) => setShowAdvanced(e.target.checked)}
                    />
                  </FormControl>

                  <Collapse in={showAdvanced}>
                    <VStack spacing={4} align="stretch">
                      <Stack
                        direction={{ base: "column", md: "row" }}
                        spacing={2}
                      >
                        <FormControl>
                          <FormLabel>{t("chat.imageSettings.steps")}</FormLabel>
                          <NumberInput
                            value={steps}
                            onChange={(_, value) => setSteps(value)}
                            min={1}
                            max={50}
                            step={1}
                          >
                            <NumberInputField bg={inputBg} />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText>
                            {t("chat.imageSettings.stepsHelpText")}
                          </FormHelperText>
                        </FormControl>

                        <FormControl>
                          <FormLabel>
                            {t("chat.imageSettings.cfgScale")}
                          </FormLabel>
                          <NumberInput
                            value={cfgScale}
                            onChange={(_, value) => setCfgScale(value)}
                            min={0}
                            max={30}
                            step={0.1}
                            precision={1}
                          >
                            <NumberInputField bg={inputBg} />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText>
                            {t("chat.imageSettings.cfgScaleHelpText")}
                          </FormHelperText>
                        </FormControl>
                      </Stack>
                      <Stack
                        direction={{ base: "column", md: "row" }}
                        spacing={2}
                      >
                        <FormControl>
                          <FormLabel>{t("chat.imageSettings.seed")}</FormLabel>
                          <HStack>
                            <NumberInput
                              value={seed}
                              onChange={(_, value) => setSeed(value)}
                              min={0}
                              max={999999}
                              step={1}
                            >
                              <NumberInputField bg={inputBg} />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>

                            <IconButton
                              aria-label={t("chat.imageSettings.randomizeSeed")}
                              icon={<FiRefreshCw />}
                              onClick={handleRandomizeSeed}
                              variant="outline"
                              size="md"
                            />
                          </HStack>
                          <FormHelperText>
                            {t("chat.imageSettings.seedHelpText")}
                          </FormHelperText>
                        </FormControl>
                        <FormControl>
                          <FormLabel>
                            {t("chat.imageSettings.scheduler")}
                          </FormLabel>
                          <Select
                            value={scheduler}
                            onChange={(e) => setScheduler(e.target.value)}
                            bg={inputBg}
                          >
                            {SCHEDULERS.map((scheduler) => (
                              <option key={scheduler} value={scheduler}>
                                {scheduler}
                              </option>
                            ))}
                          </Select>
                          <FormHelperText>
                            {t("chat.imageSettings.schedulerHelpText")}
                          </FormHelperText>
                        </FormControl>
                      </Stack>
                      {promptImageFile && (
                        <FormControl>
                          <FormLabel>
                            {t("chat.imageSettings.strength")}
                          </FormLabel>
                          <HStack>
                            <NumberInput
                              value={strength}
                              onChange={(_, value) => setStrength(value)}
                              min={0}
                              max={1}
                              step={0.1}
                            >
                              <NumberInputField bg={inputBg} />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </HStack>
                          <FormHelperText>
                            {t("chat.imageSettings.strengthHelpText")}
                          </FormHelperText>
                        </FormControl>
                      )}
                    </VStack>
                  </Collapse>
                </VStack>
              </Stack>
            )}
          </ModalBody>
        )}
        {selectedModel.includes("decor8") && (
          <ModalBody>
            {isLoading ? (
              <Box textAlign="center" py={4}>
                <Spinner />
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>{t("chat.imageSettings.decorType")}</FormLabel>
                  <Select
                    value={decorType}
                    onChange={(e) =>
                      setDecorType(e.target.value as "prime" | "stage")
                    }
                  >
                    <option value="prime">Prime</option>
                    <option value="stage">Stage</option>
                  </Select>
                  <FormHelperText>
                    {t("chat.imageSettings.decorTypeHelpText")}
                  </FormHelperText>
                </FormControl>
                {decorType === "stage" && (
                  <>
                    <FormControl>
                      <FormLabel>{t("chat.imageSettings.roomType")}</FormLabel>
                      <Select
                        value={roomType}
                        onChange={(e) =>
                          setRoomType(
                            e.target
                              .value as GenerateImageDecor8Request["roomType"]
                          )
                        }
                        bg={inputBg}
                      >
                        <option value="livingroom">
                          {t("chat.imageSettings.roomTypes.livingroom")}
                        </option>
                        <option value="kitchen">
                          {t("chat.imageSettings.roomTypes.kitchen")}
                        </option>
                        <option value="diningroom">
                          {t("chat.imageSettings.roomTypes.diningroom")}
                        </option>
                        <option value="bedroom">
                          {t("chat.imageSettings.roomTypes.bedroom")}
                        </option>
                        <option value="bathroom">
                          {t("chat.imageSettings.roomTypes.bathroom")}
                        </option>
                        <option value="kidsroom">
                          {t("chat.imageSettings.roomTypes.kidsroom")}
                        </option>
                        <option value="familyroom">
                          {t("chat.imageSettings.roomTypes.familyroom")}
                        </option>
                        <option value="readingnook">
                          {t("chat.imageSettings.roomTypes.readingnook")}
                        </option>
                        <option value="sunroom">
                          {t("chat.imageSettings.roomTypes.sunroom")}
                        </option>
                        <option value="walkincloset">
                          {t("chat.imageSettings.roomTypes.walkincloset")}
                        </option>
                        <option value="mudroom">
                          {t("chat.imageSettings.roomTypes.mudroom")}
                        </option>
                        <option value="toyroom">
                          {t("chat.imageSettings.roomTypes.toyroom")}
                        </option>
                        <option value="office">
                          {t("chat.imageSettings.roomTypes.office")}
                        </option>
                        <option value="foyer">
                          {t("chat.imageSettings.roomTypes.foyer")}
                        </option>
                        <option value="powderroom">
                          {t("chat.imageSettings.roomTypes.powderroom")}
                        </option>
                        <option value="laundryroom">
                          {t("chat.imageSettings.roomTypes.laundryroom")}
                        </option>
                        <option value="gym">
                          {t("chat.imageSettings.roomTypes.gym")}
                        </option>
                        <option value="basement">
                          {t("chat.imageSettings.roomTypes.basement")}
                        </option>
                        <option value="garage">
                          {t("chat.imageSettings.roomTypes.garage")}
                        </option>
                        <option value="balcony">
                          {t("chat.imageSettings.roomTypes.balcony")}
                        </option>
                        <option value="cafe">
                          {t("chat.imageSettings.roomTypes.cafe")}
                        </option>
                        <option value="homebar">
                          {t("chat.imageSettings.roomTypes.homebar")}
                        </option>
                        <option value="study_room">
                          {t("chat.imageSettings.roomTypes.study_room")}
                        </option>
                        <option value="front_porch">
                          {t("chat.imageSettings.roomTypes.front_porch")}
                        </option>
                        <option value="back_porch">
                          {t("chat.imageSettings.roomTypes.back_porch")}
                        </option>
                        <option value="back_patio">
                          {t("chat.imageSettings.roomTypes.back_patio")}
                        </option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>
                        {t("chat.imageSettings.designStyle")}
                      </FormLabel>
                      <Select
                        value={designStyle}
                        onChange={(e) =>
                          setDesignStyle(
                            e.target
                              .value as GenerateImageDecor8Request["designStyle"]
                          )
                        }
                        bg={inputBg}
                      >
                        <option value="minimalist">
                          {t("chat.imageSettings.designStyles.minimalist")}
                        </option>
                        <option value="scandinavian">
                          {t("chat.imageSettings.designStyles.scandinavian")}
                        </option>
                        <option value="industrial">
                          {t("chat.imageSettings.designStyles.industrial")}
                        </option>
                        <option value="boho">
                          {t("chat.imageSettings.designStyles.boho")}
                        </option>
                        <option value="traditional">
                          {t("chat.imageSettings.designStyles.traditional")}
                        </option>
                        <option value="artdeco">
                          {t("chat.imageSettings.designStyles.artdeco")}
                        </option>
                        <option value="midcenturymodern">
                          {t(
                            "chat.imageSettings.designStyles.midcenturymodern"
                          )}
                        </option>
                        <option value="coastal">
                          {t("chat.imageSettings.designStyles.coastal")}
                        </option>
                        <option value="tropical">
                          {t("chat.imageSettings.designStyles.tropical")}
                        </option>
                        <option value="eclectic">
                          {t("chat.imageSettings.designStyles.eclectic")}
                        </option>
                        <option value="contemporary">
                          {t("chat.imageSettings.designStyles.contemporary")}
                        </option>
                        <option value="frenchcountry">
                          {t("chat.imageSettings.designStyles.frenchcountry")}
                        </option>
                        <option value="rustic">
                          {t("chat.imageSettings.designStyles.rustic")}
                        </option>
                        <option value="shabbychic">
                          {t("chat.imageSettings.designStyles.shabbychic")}
                        </option>
                        <option value="vintage">
                          {t("chat.imageSettings.designStyles.vintage")}
                        </option>
                        <option value="country">
                          {t("chat.imageSettings.designStyles.country")}
                        </option>
                        <option value="modern">
                          {t("chat.imageSettings.designStyles.modern")}
                        </option>
                        <option value="asian_zen">
                          {t("chat.imageSettings.designStyles.asian_zen")}
                        </option>
                        <option value="hollywoodregency">
                          {t(
                            "chat.imageSettings.designStyles.hollywoodregency"
                          )}
                        </option>
                        <option value="bauhaus">
                          {t("chat.imageSettings.designStyles.bauhaus")}
                        </option>
                        <option value="mediterranean">
                          {t("chat.imageSettings.designStyles.mediterranean")}
                        </option>
                        <option value="farmhouse">
                          {t("chat.imageSettings.designStyles.farmhouse")}
                        </option>
                        <option value="victorian">
                          {t("chat.imageSettings.designStyles.victorian")}
                        </option>
                        <option value="gothic">
                          {t("chat.imageSettings.designStyles.gothic")}
                        </option>
                        <option value="moroccan">
                          {t("chat.imageSettings.designStyles.moroccan")}
                        </option>
                        <option value="southwestern">
                          {t("chat.imageSettings.designStyles.southwestern")}
                        </option>
                        <option value="transitional">
                          {t("chat.imageSettings.designStyles.transitional")}
                        </option>
                        <option value="maximalist">
                          {t("chat.imageSettings.designStyles.maximalist")}
                        </option>
                        <option value="arabic">
                          {t("chat.imageSettings.designStyles.arabic")}
                        </option>
                        <option value="japandi">
                          {t("chat.imageSettings.designStyles.japandi")}
                        </option>
                        <option value="retrofuturism">
                          {t("chat.imageSettings.designStyles.retrofuturism")}
                        </option>
                        <option value="artnouveau">
                          {t("chat.imageSettings.designStyles.artnouveau")}
                        </option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t("chat.imageSettings.numImages")}</FormLabel>
                      <NumberInput
                        value={numImages}
                        onChange={(_, value) => setNumImages(value)}
                        min={1}
                        max={4}
                        defaultValue={1}
                      >
                        <NumberInputField bg={inputBg} />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel>
                        {t("chat.imageSettings.scaleFactor")}
                      </FormLabel>
                      <NumberInput
                        value={scaleFactor}
                        onChange={(_, value) => setScaleFactor(value)}
                        min={1}
                        max={8}
                        step={1}
                        precision={1}
                      >
                        <NumberInputField bg={inputBg} />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>
                        {t("chat.imageSettings.colorScheme")}
                      </FormLabel>
                      <Select
                        value={colorScheme}
                        onChange={(e) =>
                          setColorScheme(
                            e.target
                              .value as GenerateImageDecor8Request["colorScheme"]
                          )
                        }
                        bg={inputBg}
                      >
                        {Array.from({ length: 21 }, (_, i) => (
                          <option key={i} value={`COLOR_SCHEME_${i}`}>
                            {t(
                              `chat.imageSettings.colorSchemes.colorScheme${i}`
                            )}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>
                        {t("chat.imageSettings.specialityDecor")}
                      </FormLabel>
                      <Select
                        value={specialityDecor}
                        onChange={(e) =>
                          setSpecialityDecor(
                            e.target
                              .value as GenerateImageDecor8Request["specialityDecor"]
                          )
                        }
                        bg={inputBg}
                      >
                        {Array.from({ length: 8 }, (_, i) => (
                          <option key={i} value={`SPECIALITY_DECOR_${i}`}>
                            {t(
                              `chat.imageSettings.specialityDecors.specialityDecor${i}`
                            )}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </VStack>
            )}
          </ModalBody>
        )}
        <ModalFooter>
          <IconButton
            aria-label={t("chat.modelInfo.close")}
            icon={<FiX />}
            onClick={onImageSettingsClose}
            variant="outline"
            colorScheme="green"
            size="sm"
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
